// Format number for display (always uses Western numerals)
export const formatNumberForDisplay = (value) => {
  if (value === null || value === undefined) return '0';
  
  // Format the number with thousands separators
  const formattedValue = typeof value === 'number' ? value.toLocaleString() : value.toString();
  
  return formattedValue;
};

// Calculate unit cost for a material
export const calculateUnitCost = (material) => {
  // Use price_with_vat if available
  const price = material.price_with_vat;
  
  if (!price || !material.quantity_per_main_unit || !material.quantity_per_piece) {
    return 0;
  }
  
  const mainUnitPrice = parseFloat(price);
  const quantityPerMainUnit = parseFloat(material.quantity_per_main_unit);
  const quantityPerPiece = parseFloat(material.quantity_per_piece);
  
  if (quantityPerMainUnit === 0 || quantityPerPiece === 0) {
    return 0;
  }
  
  return mainUnitPrice / quantityPerMainUnit / quantityPerPiece;
};

// Calculate unit cost with VAT awareness (for components that need to react to VAT changes)
export const calculateUnitCostWithVat = (material, vatPercentage) => {
  // Use price_with_vat if available, otherwise calculate from main_price with current VAT
  let price;
  
  if (material.price_with_vat) {
    // If price_with_vat exists, use it directly
    price = material.price_with_vat;
  } else {
    // No price available
    price = 0;
  }
  
  if (!price || !material.quantity_per_main_unit || !material.quantity_per_piece) {
    return 0;
  }
  
  const mainUnitPrice = parseFloat(price);
  const quantityPerMainUnit = parseFloat(material.quantity_per_main_unit);
  const quantityPerPiece = parseFloat(material.quantity_per_piece);
  
  if (quantityPerMainUnit === 0 || quantityPerPiece === 0) {
    return 0;
  }
  
  return mainUnitPrice / quantityPerMainUnit / quantityPerPiece;
};

// Get material by ID (now using API)
export const getMaterialById = async (materialId) => {
  try {
    const { materialsAPI } = await import('./api');
    const materials = await materialsAPI.getAll();
    return materials.find(material => material.id === materialId);
  } catch (error) {
    console.error('Error getting material by ID:', error);
    return null;
  }
};

// Get recipe by ID (now using API)
export const getRecipeById = async (recipeId) => {
  try {
    const { recipesAPI } = await import('./api');
    const recipes = await recipesAPI.getAll();
    return recipes.find(recipe => recipe.id === recipeId);
  } catch (error) {
    console.error('Error getting recipe by ID:', error);
    return null;
  }
};

// Calculate ingredient cost
export const calculateIngredientCost = async (ingredient) => {
  const { code, quantityUsed, type, wastePercentage } = ingredient;
  
  if (!code || !quantityUsed) return 0;
  
  // Calculate effective quantity considering waste percentage
  const waste = parseFloat(wastePercentage) || 0;
  const effectiveQuantity = parseFloat(quantityUsed) * (1 + waste / 100);
  
  if (type === 'rawMaterial') {
    const material = await getMaterialById(code);
    if (!material) return 0;
    
    const unitCost = calculateUnitCost(material);
    return unitCost * effectiveQuantity;
  } else if (type === 'recipe') {
    const recipe = await getRecipeById(code);
    if (!recipe) return 0;
    
    const costPerServing = calculateRecipeCostPerServing(recipe);
    return costPerServing * effectiveQuantity;
  }
  
  return 0;
};

// Calculate total recipe cost
export const calculateRecipeTotalCost = async (recipe) => {
  if (!recipe.ingredients || recipe.ingredients.length === 0) {
    return 0;
  }
  
  let total = 0;
  for (const ingredient of recipe.ingredients) {
    total += await calculateIngredientCost(ingredient);
  }
  
  return total;
};

// Calculate cost per serving for a recipe
export const calculateRecipeCostPerServing = (recipe) => {
  const totalCost = recipe.total_cost || 0;
  const numberOfServings = parseFloat(recipe.servings) || 1;
  
  if (numberOfServings === 0) return 0;
  
  return totalCost / numberOfServings;
};

// Get ingredient details for display
export const getIngredientDetails = async (ingredient) => {
  const { code, type } = ingredient;
  
  if (type === 'rawMaterial') {
    const material = await getMaterialById(code);
    if (!material) return { name: 'Unknown', name_ar: '', unit: '', cost: 0 };
    
    return {
      name: material.name_en,
      name_ar: material.name_ar,
      unit: material.sub_unit || 'piece',
      cost: calculateUnitCost(material)
    };
  } else if (type === 'recipe') {
    const recipe = await getRecipeById(code);
    if (!recipe) return { name: 'Unknown', name_ar: '', unit: 'serving', cost: 0 };
    
    return {
      name: recipe.name_en,
      name_ar: recipe.name_ar,
      unit: 'serving',
      cost: calculateRecipeCostPerServing(recipe)
    };
  }
  
  return { name: 'Unknown', name_ar: '', unit: '', cost: 0 };
};

// Check if material is in use
export const isMaterialInUse = async (materialId) => {
  try {
    const { recipesAPI } = await import('./api');
    const recipes = await recipesAPI.getAll();
    
    return recipes.some(recipe => 
      recipe.ingredients && recipe.ingredients.some(ingredient => 
        ingredient.code === materialId && ingredient.type === 'rawMaterial'
      )
    );
  } catch (error) {
    console.error('Error checking if material is in use:', error);
    return false;
  }
};

// Check if recipe is in use
export const isRecipeInUse = async (recipeId) => {
  try {
    const { recipesAPI } = await import('./api');
    const recipes = await recipesAPI.getAll();
    
    return recipes.some(recipe => 
      recipe.ingredients && recipe.ingredients.some(ingredient => 
        ingredient.code === recipeId && ingredient.type === 'recipe'
      )
    );
  } catch (error) {
    console.error('Error checking if recipe is in use:', error);
    return false;
  }
};

// Format currency with thousands separators
// Note: This function is deprecated. Use the formatCurrency from CurrencyContext instead.
export const formatCurrency = (amount) => {
  const num = parseFloat(amount);
  if (isNaN(num)) return '0.00 ﷼';
  
  // Format with thousands separators and 2 decimal places
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${formatted} ﷼`;
};

// Format numbers with thousands separators (for non-currency values)
export const formatNumber = (number, decimals = 2) => {
  const num = parseFloat(number);
  if (isNaN(num)) return '0';
  
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// Format percentages with thousands separators
export const formatPercentage = (value, decimals = 1) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0%';
  
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  
  return `${formatted}%`;
};

// Format integers with thousands separators
export const formatInteger = (number) => {
  const num = parseInt(number);
  if (isNaN(num)) return '0';
  
  return num.toLocaleString('en-US');
};

// Format number for display in input fields (removes thousands separators for editing)
export const formatNumberForInput = (number, decimals = 2) => {
  const num = parseFloat(number);
  if (isNaN(num)) return '';
  
  const result = num.toFixed(decimals);
  
  
  return result;
};

// Parse formatted number back to numeric value
export const parseFormattedNumber = (formattedString) => {
  if (!formattedString || typeof formattedString !== 'string') return 0;
  
  // Remove thousands separators and parse
  const cleanString = formattedString.replace(/,/g, '');
  const num = parseFloat(cleanString);
  
  return isNaN(num) ? 0 : num;
};

// Get top used materials by cost
export const getTopUsedMaterials = async () => {
  try {
    const { recipesAPI, materialsAPI } = await import('./api');
    const recipes = await recipesAPI.getAll();
    const materials = await materialsAPI.getAll();
    
    const materialUsage = {};
    
    // Count usage and calculate total cost for each material
    recipes.forEach(recipe => {
      if (recipe.ingredients) {
        recipe.ingredients.forEach(ingredient => {
          if (ingredient.type === 'rawMaterial') {
            const material = materials.find(m => m.id === ingredient.code);
            if (material) {
              if (!materialUsage[material.id]) {
                materialUsage[material.id] = {
                  material,
                  totalQuantity: 0,
                  totalCost: 0,
                  usageCount: 0
                };
              }
              
              const quantity = parseFloat(ingredient.quantityUsed) || 0;
              const wastePercentage = parseFloat(ingredient.wastePercentage) || 0;
              const effectiveQuantity = quantity * (1 + wastePercentage / 100);
              const unitCost = calculateUnitCost(material);
              const cost = effectiveQuantity * unitCost;
              
              materialUsage[material.id].totalQuantity += quantity;
              materialUsage[material.id].totalCost += cost;
              materialUsage[material.id].usageCount += 1;
            }
          }
        });
      }
    });
    
    // Convert to array and sort by total cost
    return Object.values(materialUsage)
      .sort((a, b) => b.totalCost - a.totalCost)
      .slice(0, 10);
  } catch (error) {
    console.error('Error getting top used materials:', error);
    return [];
  }
}; 